import React, { useState, useMemo, useCallback } from 'react';
import { LivenessView } from '@imaginemos-ae/liveness-ui-component';
import './liveness-sdk.css';
import { useUrlParams } from './hooks/useUrlParams';
import { useConfig } from './hooks/useConfig';
import { buildSuccessData } from './utils/mappers';
import { PLATFORMS } from './constants';

const isMobile = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const sendToHost = (platform, isMovil, data, commConfig) => {
    const msg = JSON.stringify(data);
    
    if (isMovil || platform === PLATFORMS.REACT_NATIVE) {
        window.ReactNativeWebView?.postMessage(msg);
        return;
    }

    switch (platform) {
        case PLATFORMS.IOS:
            window.webkit?.messageHandlers?.messageHandlersName?.postMessage(msg);
            break;
        case PLATFORMS.ANDROID:
            window.ResponseAdo?.handleEvent(msg);
            break;
        default:
            window.parent.postMessage(msg, commConfig?.platforms?.web?.targetOrigin || '*');
    }
};

const sendCloseToHost = (platform, isMovil, commConfig) => {
    const closeEvent = commConfig?.events?.onClose || { type: 'success', status: 'back' };
    const origin = commConfig?.origin || 'liveness';
    
    if (platform === PLATFORMS.ANDROID) {
        window.FinishClose?.goBack();
        return;
    }

    const data = platform === PLATFORMS.WEB && !isMovil
        ? { origin, ...closeEvent, data: '' }
        : { ...closeEvent, data: '' };

    sendToHost(platform, isMovil, data, commConfig);
};

export default function LivenessV6Wrapper() {
    const [finished, setFinished] = useState(false);
    const params = useUrlParams();
    const { config, loading } = useConfig(params.clientId);
    const mobile = useMemo(() => isMobile(), []);
    
    const commConfig = config?.communication;
    const origin = commConfig?.origin || 'liveness';

    const handleReady = useCallback(() => {
        if (params.emitReadyCam !== 'true') return;
        const readyMsg = commConfig?.events?.onReady?.message || 'ado-cam-ready';
        
        if (params.isMovil || params.platform === PLATFORMS.REACT_NATIVE) {
            window.ReactNativeWebView?.postMessage(readyMsg);
        } else if (params.platform === PLATFORMS.IOS) {
            window.webkit?.messageHandlers?.messageHandlersName?.postMessage(readyMsg);
        } else if (params.platform === PLATFORMS.ANDROID) {
            window.ResponseAdo?.handleEvent(readyMsg);
        } else {
            window.parent.postMessage(readyMsg, '*');
        }
    }, [params, commConfig]);

    const handleSuccess = useCallback((result) => {
        const successEvent = commConfig?.events?.onSuccess || { type: 'success', status: 'success' };
        const data = buildSuccessData(result);
        
        const msg = params.platform === PLATFORMS.WEB && !params.isMovil
            ? { origin, ...successEvent, data }
            : { ...successEvent, data };

        sendToHost(params.platform, params.isMovil, msg, commConfig);
        setFinished(true);
    }, [params, commConfig, origin]);

    const handleError = useCallback((error) => {
        const errorEvent = commConfig?.events?.onError || { type: 'error-intern', step: 'permissionDenied' };
        
        const msg = params.platform === PLATFORMS.WEB && !params.isMovil
            ? { origin, ...errorEvent, data: 'permissionDenied', message: error.message }
            : { ...errorEvent, data: 'permissionDenied', message: error.message };

        sendToHost(params.platform, params.isMovil, msg, commConfig);
    }, [params, commConfig, origin]);

    const handleClose = useCallback(() => {
        sendCloseToHost(params.platform, params.isMovil, commConfig);
        setFinished(true);
    }, [params, commConfig]);

    if (loading || finished) return null;

    const platform = mobile ? 'mobile' : 'desktop';
    const platformConfig = config?.[platform] || {};
    const challenges = params.challenges?.split(',') 
        || platformConfig.challenges 
        || ['smile'];
    const canSelectCamera = platformConfig.showCameraSelection ?? false;

    // DEBUG: verificar valores
    console.log('DEBUG v6 params.apiUrl:', params.apiUrl);
    console.log('DEBUG v6 config:', JSON.stringify(config, null, 2));

    return (
        <LivenessView
            config={{
                apiUrl: params.apiUrl,
                clientId: params.clientId,
                transactionId: params.transactionId,
                
                timeout: params.timeout || config?.timeout || 60,
                camera: config?.camera || 'user',
                videoQuality: platformConfig.videoQuality || 'medium',
                autoChallengeCount: config?.autoChallengeCount || 2,
                
                challenges,
                features: config?.features,
                screenFlash: canSelectCamera ? null : platformConfig.screenFlash,
                shape: platformConfig.shape || 'oval',
                
                logoUrl: params.logoUrl || config?.logoUrl,
                lottieUrl: config?.lottieUrl,
                processingText: config?.processingText,
                
                requireGeolocation: platformConfig.requireGeolocation ?? false,
                requireMicrophone: platformConfig.requireMicrophone ?? false,
                closeOnFocusLost: config?.closeOnFocusLost ?? false,
                showFaceMesh: config?.showFaceMesh ?? false,
                showCameraSelection: canSelectCamera,
                
                enableFacialAttributeValidation: config?.enableFacialAttributeValidation ?? false,
                facialAttributeThreshold: config?.facialAttributeThreshold ?? 0.5,
                
                messagePosition: platformConfig.messagePosition || 'bottom',
                delegate: platformConfig.delegate || 'GPU',
                
                assetsPath: config?.assetsPath,
                modelPath: config?.modelPath,
                
                ui: config?.ui,
                styles: config?.styles,
                messages: config?.messages
            }}
            callbacks={{
                onReady: handleReady,
                onSuccess: handleSuccess,
                onError: handleError,
                onClose: handleClose
            }}
        />
    );
}
